(ns ui.view.card.activities.event
  (:require [re-frame.core :as rf]
            [potpuri.core :as p]
            [ui.view.card.model :as common-model]
            [ui.view.card.activities.model :as model]
            [ui.fx.interval :as interval]
            [clojure.string :as string]
            [ui.cofx.response :as response-cofx]))

;; One minute
(def ACTIVITY-POLLING-DELAY 60000)
(def EMAIL-POLLING-DELAY 60000)

(defn polling-inverval-id
  [prefix card-state-path]
  (->> card-state-path
       (mapv str)
       (cons prefix)
       (string/join "-")))

(defn email-polling-interval-id
  [card-state-path]
  (polling-inverval-id "email-polling" card-state-path))

(defn activity-polling-interval-id
  [card-state-path]
  (polling-inverval-id "activity-polling" card-state-path))

(rf/reg-event-db
 ::set-selected-activity-tab
 (fn [db [_ {:keys [card-state-path] :as _ctx} tab-value]]
   (assoc-in db (model/selected-activity-tab-full-path card-state-path) tab-value)))

(rf/reg-event-db
 ::set-activity-tag-filters
 (fn [db [_ {:keys [card-state-path] :as _ctx} tag-ids]]
   (assoc-in db (model/activity-tag-filters-full-path card-state-path) (vec tag-ids))))

(rf/reg-event-db
 ::toggle-collapse-activity
 (fn [db [_ {:keys [card-state-path] :as _ctx} activity-id]]
   (let [collapse-activity-state (get-in db (model/collapse-activity-state-full-path card-state-path))
         explicitly-set? (contains? collapse-activity-state activity-id)
         prev-value (if explicitly-set?
                      (get-in db (model/collapse-activity-full-path card-state-path activity-id))
                      (get-in db (model/collapse-all-activities-full-path card-state-path)))
         new-value (not prev-value)]
     (assoc-in db (model/collapse-activity-full-path card-state-path activity-id) new-value))))

(rf/reg-event-db
 ::toggle-collapse-all-activities
 (fn [db [_ {:keys [card-state-path] :as _ctx}]]
   (-> db
       (update-in (model/collapse-all-activities-full-path card-state-path) not)
       (p/dissoc-in (model/collapse-activity-state-full-path card-state-path)))))

(defn fetch-data-failure-event-fx-handler
  [{:keys [stale-response?]} [_ctx _resp]]
  (when-not stale-response?
    ;; TODO
    (js/console.log "Fetch card data failure not implemented")))

(defn fetch-emails-success-event-fx-handler
  [{:keys [db stale-response?]}
   [{:keys [card-state-path start-polling?] :as ctx}
    {:keys [body] :as _resp}]]
  (when-not stale-response?
    (let [entities (-> body :data :entities)
          entities-by-id (p/index-by :db/id entities)
          new-db (-> db
                     (assoc-in (model/emails-loaded-full-path card-state-path) true)
                     (assoc-in (model/loading-emails-full-path card-state-path) false)
                     (common-model/merge-entities card-state-path entities-by-id))]
      (merge
       {:db new-db}
       (when start-polling?
         {::interval/interval {:id (email-polling-interval-id card-state-path)
                               :delay-ms EMAIL-POLLING-DELAY
                               :event [::fetch-emails (dissoc ctx :start-polling?)]}})))))

(defn fetch-emails-event-fx-handler
  [_cofx [{:keys [card entity-id signal] :as ctx}]]
  {:fetch {:uri "/card/query"
           :method :post
           :body {:id     :get-entity-emails
                  :params {:entity-id entity-id
                           :card card}}
           :signal signal
           :on-success [::fetch-emails-success ctx]
           :on-failure [::fetch-data-failure ctx]}})

(defn fetch-activities-success-event-fx-handler
  [{:keys [db stale-response?]}
   [{:keys [card-state-path start-polling?] :as ctx}
    {:keys [body] :as _resp}]]
  (when-not stale-response?
    (let [entities (-> body :data :entities)
          entities-by-id (p/index-by :db/id entities)
          new-db (-> db
                     (assoc-in (model/loading-activities-full-path card-state-path) false)
                     (assoc-in (model/activities-loaded-full-path card-state-path) true)
                     (common-model/set-loading-error card-state-path false)
                     (common-model/merge-entities card-state-path entities-by-id))]
      (merge
       {:db new-db}
       (when start-polling?
         {::interval/interval {:id (activity-polling-interval-id card-state-path)
                               :delay-ms ACTIVITY-POLLING-DELAY
                               :event [::fetch-activities (dissoc ctx :start-polling?)]}})))))

(defn fetch-activities-event-fx-handler
  [_cofx [{:keys [card entity-id signal] :as ctx}]]
  {:fetch {:uri "/card/query"
           :method :post
           :body {:id     :get-entity-activities
                  :params {:entity-id entity-id
                           :card card}}
           :signal signal
           :on-success [::fetch-activities-success ctx]
           :on-failure [::fetch-data-failure ctx]}})

(defn fetch-card-data-event-fx-handler
  [_cofx [{:keys [subscribe-to-realtime-changes?] :as ctx}]]
  (let [ctx (assoc ctx :start-polling? subscribe-to-realtime-changes?)]
    {:fx [[:dispatch [::fetch-activities ctx]]
          [:dispatch [::fetch-emails ctx]]]}))

(defn cleanup-event-fx-handler
  [_cofx [{:keys [card-state-path] :as _ctx}]]
  (let [activity-polling-interval-id (activity-polling-interval-id card-state-path)
        email-polling-interval-id (email-polling-interval-id card-state-path)]
    {:fx [[::interval/clear-interval activity-polling-interval-id]
          [::interval/clear-interval email-polling-interval-id]]}))

(rf/reg-event-fx
 ::fetch-card-data
 [rf/trim-v]
 fetch-card-data-event-fx-handler)

(rf/reg-event-fx
 ::fetch-emails
 [rf/trim-v]
 fetch-emails-event-fx-handler)

(rf/reg-event-fx
 ::fetch-emails-success
 [rf/trim-v]
 fetch-emails-success-event-fx-handler)

(rf/reg-event-fx
 ::fetch-activities
 [rf/trim-v
  response-cofx/stale-response?]
 fetch-activities-event-fx-handler)

(rf/reg-event-fx
 ::fetch-activities-success
 [rf/trim-v
  response-cofx/stale-response?]
 fetch-activities-success-event-fx-handler)

(rf/reg-event-fx
 ::fetch-data-failure
 [rf/trim-v
  response-cofx/stale-response?]
 fetch-data-failure-event-fx-handler)

(rf/reg-event-fx
 ::cleanup
 [rf/trim-v]
 cleanup-event-fx-handler)
